import React from 'react';
import PropTypes from 'prop-types';
import { Loc } from 'redux-react-i18n';
import { Link } from 'react-router-dom';
import Media from 'react-media';
import ProgressiveImage from 'react-progressive-image';
import Dotdotdot from 'react-dotdotdot';
import styled from 'styled-components';
import Loader from 'components/mediabay-ui/components/Loader';
import Button from 'components/mediabay-ui/components/Button';
import Title from 'components/mediabay-ui/components/Title';
import Carousel from 'components/mediabay-ui/components/Carousel';
import CarouselTitle from './common/CarouselTitle';
import CarouselContainer from './common/CarouselContainer';
import CarouselEmpty from './common/CarouselEmpty';
import MediaDetails from '../DetailsComponent/MediaDetails';

const MediaCoverWrapper = styled.a`
  position: relative;

  &:after {
    content: '${props => props.limit}';
    position: ${props => (props.limit ? 'absolute' : 'static')};
    top: 0;
    right: 0;
    width: 45px;
    height: 30px;
    background: #0773FF;
    text-align: center;
    line-height: 30px;
    font-weight: bold;
    border-radius: 0 0 0 10px;
  }
`;

class MediasCarousel extends React.Component {
  static propTypes = {
    landscapeCover: PropTypes.bool,
    currentLanguage: PropTypes.string.isRequired,
    title: PropTypes.node.isRequired,
    buttonContent: PropTypes.node.isRequired,
    titleRoute: PropTypes.string.isRequired,
    itemRoute: PropTypes.string.isRequired,
    getMedias: PropTypes.func.isRequired,
    getMediaDetails: PropTypes.func,
    mediasData: PropTypes.shape({
      status: PropTypes.number,
      data: PropTypes.arrayOf(PropTypes.object),
    }).isRequired,
    mediaDetailsData: PropTypes.shape({
      status: PropTypes.number,
      data: PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.object,
        PropTypes.array,
        PropTypes.bool,
      ])),
    }),
  }

  static defaultProps = {
    landscapeCover: false,
    getMediaDetails: null,
    mediaDetailsData: {
      status: null,
      data: null,
    },
  }

  constructor(props) {
    super(props);

    const { mediasData } = this.props;

    this.state = {
      isMediasFetching: true,
      isMediaDetailsVisible: false,
      mediasData,
    };

    this.closeMediaDetailsHandler = this.closeMediaDetailsHandler.bind(this);
    this.mediaCarouselOutsideClickHandler = this.mediaCarouselOutsideClickHandler.bind(this);
    this.mediaClickHandler = this.mediaClickHandler.bind(this);
  }

  componentDidMount() {
    const { getMedias } = this.props;
    const { mediasData } = this.state;

    document.addEventListener('click', this.mediaCarouselOutsideClickHandler);

    if (mediasData.data.length === 0) {
      return getMedias();
    }

    return null;
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.mediaCarouselOutsideClickHandler);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState.mediasData) {
      if (
        prevState.mediasData.status === 200
        || prevState.mediasData.status !== nextProps.mediasData.status
      ) {
        return {
          isMediasFetching: false,
          mediasData: nextProps.mediasData,
        };
      }
    }

    return null;
  }

  mediaCarouselOutsideClickHandler(e) {
    // Ignore clicks on the component itself
    if (!this.mediaCarouselRef.contains(e.target)) {
      this.setState({
        isMediaDetailsVisible: false,
      });
    }
  }

  mediaClickHandler(e) {
    e.preventDefault();

    const { landscapeCover, getMediaDetails } = this.props;
    setTimeout(() => {
      window.scroll({
        top: this.mediaCarouselRef.offsetTop + (landscapeCover ? 100 : 300),
        left: 0,
        behavior: 'smooth',
      });
    }, 500);
    let { id } = e.target.parentNode.dataset;
    id = parseInt(id, 10);

    getMediaDetails(id);

    this.setState({
      isMediaDetailsVisible: true,
    });
  }

  closeMediaDetailsHandler() {
    this.setState({
      isMediaDetailsVisible: false,
    });
  }

  render() {
    const { isMediasFetching, isMediaDetailsVisible, mediasData } = this.state;
    const {
      currentLanguage,
      landscapeCover,
      mediaDetailsData,
      itemRoute,
      title,
      titleRoute,
      buttonContent,
    } = this.props;

    let carouselContent = null;

    if (isMediasFetching) {
      carouselContent = (
        <CarouselEmpty>
          <Loader />
        </CarouselEmpty>
      );
    } else if (isMediasFetching === false && mediasData.status !== 200) {
      carouselContent = (
        <CarouselEmpty>
          <Loc locKey="loadingError" />
        </CarouselEmpty>
      );
    } else if (mediasData.data.length === 0) {
      carouselContent = (
        <CarouselEmpty>
          <Loc locKey="noMediaData" />
        </CarouselEmpty>
      );
    } else {
      carouselContent = (
        <Carousel
          containerOffset={65}
          childWidth={200}
          detailsComponent={(
            <MediaDetails
              isVisible={isMediaDetailsVisible}
              mediaDetailsData={mediaDetailsData}
              closeDetailsHandler={this.closeMediaDetailsHandler}
              playRoute={itemRoute}
              currentLanguage={currentLanguage}
            />
          )}
        >
          {
            mediasData.data.map(media => (
              <MediaCoverWrapper
                key={media.id}
                data-id={media.id}
                href={`/${itemRoute}/${media.id}`}
                limit={media.yearLimit}
                onClick={this.mediaClickHandler}
              >
                <ProgressiveImage
                  delay={500}
                  src={media.picture.replace(/.png|.jpg|.jpeg/, match => `.200${match}`)}
                >
                  {(src, loading) => (
                    loading ? (
                      <div style={{ width: 200, height: landscapeCover ? 133 : 355, background: '#1e1e1e' }} />
                    ) : (
                      <img
                        alt={media.title[currentLanguage] || media.title['ru']}
                        src={src}
                        width={200}
                        height={landscapeCover ? 133 : 355}
                        style={{ objectFit: 'cover' }}
                      />
                    )
                  )}
                </ProgressiveImage>
                <Dotdotdot clamp={2} useNativeClamp>
                  <Title>
                    {media.title[currentLanguage] || media.title['ru']}
                  </Title>
                </Dotdotdot>
              </MediaCoverWrapper>
            ))
          }
        </Carousel>
      );
    }

    return (
      <section ref={(mediaCarouselRef) => { this.mediaCarouselRef = mediaCarouselRef; }}>
        <CarouselTitle level={2}>
          <Link to={`/${titleRoute}`}>
            {title}
          </Link>
          <Media
            query="(min-device-width: 500px)"
            render={() => (
              <Link to={`/${titleRoute}`} style={{ marginRight: 25 }}>
                <Button>
                  {buttonContent}
                </Button>
              </Link>
            )}
          />
        </CarouselTitle>
        <CarouselContainer>
          {carouselContent}
        </CarouselContainer>
      </section>
    );
  }
}

export default MediasCarousel;
